import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import {
  LayoutDashboard, Users, UserRound, CalendarCheck, LineChart, ClipboardList,
  Trophy, Swords, CreditCard, MessagesSquare, UserPlus, FileBarChart, Sparkles,
  Settings, LogOut, ShieldCheck, Menu, X, Vote, Medal, Presentation, Download, FolderOpen, Newspaper,
} from "lucide-react";
import { useState, useEffect } from "react";
import { usePwaInstall } from "@/hooks/usePwaInstall";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { VerifyBanner } from "@/components/VerifyBanner";
import api from "@/lib/api";

const NAV = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true, tid: "nav-dashboard" },
  { to: "/app/teams", label: "Teams", icon: Users, tid: "nav-teams" },
  { to: "/app/players", label: "Athletes", icon: UserRound, tid: "nav-players" },
  { to: "/app/attendance", label: "Attendance", icon: CalendarCheck, tid: "nav-attendance" },
  { to: "/app/performance", label: "Performance", icon: LineChart, tid: "nav-performance" },
  { to: "/app/training", label: "Training", icon: ClipboardList, tid: "nav-training" },
  { to: "/app/matches", label: "Matches", icon: Swords, tid: "nav-matches" },
  { to: "/app/tactics", label: "Tactics", icon: Presentation, tid: "nav-tactics" },
  { to: "/app/rankings", label: "Rankings", icon: Trophy, tid: "nav-rankings" },
  { to: "/app/tournaments", label: "Tournaments", icon: Medal, tid: "nav-tournaments" },
  { to: "/app/staff", label: "Staff", icon: UserPlus, tid: "nav-staff" },
  { to: "/app/payments", label: "Payments", icon: CreditCard, tid: "nav-payments" },
  { to: "/app/messages", label: "Messages", icon: MessagesSquare, tid: "nav-messages" },
  { to: "/app/community", label: "Community", icon: Vote, tid: "nav-community" },
  { to: "/app/registrations", label: "Registrations", icon: UserPlus, tid: "nav-registrations" },
  { to: "/app/reports", label: "Reports", icon: FileBarChart, tid: "nav-reports" },
  { to: "/app/library", label: "Library", icon: FolderOpen, tid: "nav-library" },
  { to: "/app/blog", label: "Blog", icon: Newspaper, tid: "nav-blog" },
  { to: "/app/ai", label: "AI Coach", icon: Sparkles, tid: "nav-ai" },
  { to: "/app/settings", label: "Club Settings", icon: Settings, tid: "nav-settings" },
];

const BOTTOM = [
  { to: "/app", label: "Home", icon: LayoutDashboard, end: true, tid: "bottomnav-dashboard" },
  { to: "/app/players", label: "Athletes", icon: UserRound, tid: "bottomnav-players" },
  { to: "/app/matches", label: "Matches", icon: Swords, tid: "bottomnav-matches" },
  { to: "/app/payments", label: "Payments", icon: CreditCard, tid: "bottomnav-payments" },
];

export default function DashboardLayout() {
  const { user, club, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const location = useLocation();
  useEffect(() => {
    if (!user || user.role === "parent") return;
    const load = () => api.get("/notifications/unread").then((r) => setUnread(r.data.total || 0)).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [user, location.pathname]);

  const doLogout = async () => { await logout(); navigate("/"); };
  const { canInstall, standalone, ios, promptInstall } = usePwaInstall();
  const installApp = async () => {
    setOpen(false);
    if (await promptInstall()) return;
    if (ios) toast("Install SphereHyx", { description: "Tap the Share icon in Safari, then choose 'Add to Home Screen'." });
    else toast("Install SphereHyx", { description: "Open your browser menu (⋮) and choose 'Install app' / 'Add to Home screen'." });
  };

  const nav = [...NAV];

  const SideContent = (
    <div className="flex flex-col h-full">
      <div className="px-5 h-16 flex items-center border-b border-border">
        <Logo size={30} />
      </div>
      <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-0.5">
        {nav.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            data-testid={n.tid}
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-150 ${
                isActive
                  ? "bg-primary/15 text-primary"
                  : "text-muted-foreground hover:bg-accent hover:text-foreground"
              }`
            }
          >
            <n.icon size={18} strokeWidth={2} />
            <span className="flex-1">{n.label}</span>
            {n.tid === "nav-messages" && unread > 0 && (
              <span className="ml-auto min-w-5 h-5 px-1.5 rounded-full bg-primary text-white text-[11px] font-semibold flex items-center justify-center" data-testid="messages-unread-badge">{unread}</span>
            )}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-border">
        {!standalone && (
          <button onClick={installApp} data-testid="install-app-btn" className="w-full mb-2 flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium brand-gradient text-white hover:opacity-90 transition-opacity">
            <Download size={18} strokeWidth={2} />
            <span className="flex-1 text-left">Install app</span>
            {canInstall && <span className="text-[10px] bg-white/25 rounded-full px-1.5 py-0.5">1-tap</span>}
          </button>
        )}
        <div className="flex items-center gap-3 px-2 py-2">
          <div className="h-9 w-9 rounded-full brand-gradient flex items-center justify-center text-white font-semibold text-sm">
            {user?.name?.[0]?.toUpperCase() || "?"}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold truncate">{user?.name}</p>
            <p className="text-xs text-muted-foreground truncate capitalize">{user?.role}</p>
          </div>
          <button onClick={doLogout} data-testid="logout-button" className="text-muted-foreground hover:text-destructive transition-colors">
            <LogOut size={18} />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-background grain">
      <aside className="hidden lg:flex w-64 shrink-0 border-r border-border bg-card/40 flex-col fixed h-screen z-30">
        {SideContent}
      </aside>
      {open && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          <div className="absolute inset-0 bg-black/70" onClick={() => setOpen(false)} />
          <aside className="relative w-64 bg-card border-r border-border">{SideContent}</aside>
        </div>
      )}
      <div className="flex-1 lg:ml-64 min-w-0">
        <header className="h-16 border-b border-border glass sticky top-0 z-20 flex items-center justify-between px-4 lg:px-8">
          <div className="flex items-center gap-3">
            <button className="lg:hidden text-foreground" onClick={() => setOpen(true)} data-testid="mobile-menu-button">
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
            {user?.role === "admin" && !club ? (
              <Logo size={30} />
            ) : (
              <div className="flex items-center gap-2">
                {club?.logo ? (
                  <img src={club.logo} alt="" className="h-8 w-8 rounded-md object-cover border border-border" />
                ) : (
                  <div className="h-8 w-8 rounded-md brand-gradient" />
                )}
                <div>
                  <p className="font-heading font-bold leading-tight" data-testid="club-name-header">{club?.name || "Your Club"}</p>
                  <p className="text-[11px] text-muted-foreground capitalize">{club?.sport?.replace("_", " ")} · {club?.plan} plan</p>
                </div>
              </div>
            )}
          </div>
          <Button asChild size="sm" variant="outline" className="hidden sm:flex" data-testid="upgrade-link-header">
            <NavLink to="/app/payments">Upgrade</NavLink>
          </Button>
        </header>
        <main className="p-4 lg:p-8 pb-24 lg:pb-8 max-w-[1400px] mx-auto">
          <VerifyBanner />
          <Outlet />
        </main>
        <nav className="lg:hidden fixed bottom-0 inset-x-0 z-30 glass border-t border-border flex items-stretch justify-around h-16" data-testid="mobile-bottom-nav">
          {BOTTOM.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.tid}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-0.5 flex-1 text-[10px] font-medium transition-colors ${
                  isActive ? "text-primary" : "text-muted-foreground hover:text-foreground"
                }`
              }
            >
              <n.icon size={20} strokeWidth={2} />
              <span>{n.label}</span>
            </NavLink>
          ))}
          <button onClick={() => setOpen(true)} data-testid="bottomnav-more" className="flex flex-col items-center justify-center gap-0.5 flex-1 text-[10px] font-medium text-muted-foreground hover:text-foreground transition-colors">
            <Menu size={20} strokeWidth={2} />
            <span>More</span>
          </button>
        </nav>
      </div>
    </div>
  );
}
